import { type Functions, httpsCallable } from 'firebase/functions';
import {
  type Day,
  type MealEntry,
  SLOTS,
  type Slot,
  type WeekPlan,
} from '@/service/weekplan/types';

// Matches the generateWeekPlan Cloud Function
export const MAX_WISH_LENGTH = 200;

export type ProposedMeals = Partial<
  Record<Day, Partial<Record<Slot, MealEntry>>>
>;

export interface PlanRequest {
  weekStart: string;
  // The empty slots to fill, per day
  slots: Partial<Record<Day, Slot[]>>;
  wish: string;
}

export interface Proposal {
  meals: ProposedMeals;
  count: number;
}

export interface WeekPlannerInterface {
  plan(request: PlanRequest): Promise<Proposal>;
}

// Asks Gemini, through the generateWeekPlan Cloud Function, for meals from
// the user's own recipes. Nothing is saved until the user accepts them.
export default class WeekPlanner implements WeekPlannerInterface {
  constructor(private readonly functions: Functions) {}

  async plan(request: PlanRequest): Promise<Proposal> {
    const generate = httpsCallable<PlanRequest, Proposal>(
      this.functions,
      'generateWeekPlan',
    );
    return (await generate(request)).data;
  }
}

// The chosen slots of the chosen days that have no meal yet
export function emptySlots(
  plan: WeekPlan,
  days: Day[],
  slots: Slot[],
): Partial<Record<Day, Slot[]>> {
  const empty: Partial<Record<Day, Slot[]>> = {};
  for (const day of days) {
    const open = SLOTS.filter(
      (slot) => slots.includes(slot) && !plan.meals[day]?.[slot],
    );
    if (open.length) empty[day] = open;
  }
  return empty;
}

export function slotCount(slots: Partial<Record<Day, unknown[]>>): number {
  return Object.values(slots).reduce((sum, day) => sum + day.length, 0);
}

// The function's own message where it explains what to do; a general one
// otherwise (network errors, Gemini failing)
export function planErrorMessage(error: unknown): string {
  const { code, message } = (error ?? {}) as {
    code?: string;
    message?: string;
  };
  return [
    'functions/resource-exhausted',
    'functions/failed-precondition',
  ].includes(code ?? '') && message
    ? message
    : 'Could not plan your week. Please try again.';
}
